import type { QueryObject } from '@/shared/lib/serializeQuery';
import { isDynamicFilterGroup } from './filtersModel';
import { isSearchFilterGroup } from './searchFilters';
import { SERP_FILTER_GROUPS } from '../config/filters';
import type { FilterGroupId, FiltersModel, SerpBudgetFilterGroup, SerpOptionsFilterGroup } from '../model/types';

type OwnedFilterGroup = SerpOptionsFilterGroup | SerpBudgetFilterGroup;

const FILTER_QUERY_PREFIX = 'f_';

const BUDGET_MIN_KEY = 'f_minPrice';

const BUDGET_MAX_KEY = 'f_maxPrice';

const ID_SEPARATOR = '.';

function getOwnedGroups(): OwnedFilterGroup[] {
  return SERP_FILTER_GROUPS.filter((group): group is OwnedFilterGroup => !isSearchFilterGroup(group));
}

function getFlagKey(groupId: FilterGroupId): string {
  return `${FILTER_QUERY_PREFIX}${groupId}`;
}

function getGroupTokens(group: OwnedFilterGroup): string[] {
  return 'options' in group && group.options ? group.options.map((option) => option.key) : [];
}

function isFlagSet(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  const text = typeof value === 'string' ? value : '';

  return Boolean(text) && text !== 'false' && text !== '0';
}

function toAmount(value: unknown): number {
  const amount = Number(typeof value === 'string' ? value : '');

  return Number.isFinite(amount) && amount > 0 ? Math.round(amount) : 0;
}

function toKeys(value: unknown): string[] {
  const text = typeof value === 'string' ? value : '';

  return text ? text.split(ID_SEPARATOR).filter(Boolean) : [];
}

export function toFiltersQuery(filtersModel: FiltersModel): QueryObject {
  const query: QueryObject = {};

  getOwnedGroups().forEach((group) => {
    if (group.control === 'budget') {
      if (filtersModel.budget.min) query[BUDGET_MIN_KEY] = filtersModel.budget.min;
      if (filtersModel.budget.max) query[BUDGET_MAX_KEY] = filtersModel.budget.max;

      return;
    }

    if (isDynamicFilterGroup(group)) {
      const ids = filtersModel[group.id];
      if (ids.length) query[`${FILTER_QUERY_PREFIX}${group.id}`] = ids.join(ID_SEPARATOR);

      return;
    }

    if (group.control === 'checkbox') {
      if (filtersModel[group.id]) query[getFlagKey(group.id)] = true;

      return;
    }

    const value = filtersModel[group.id];
    const selected = Array.isArray(value) ? value : value ? [value] : [];
    selected.forEach((token) => {
      query[`${FILTER_QUERY_PREFIX}${token}`] = true;
    });
  });

  return query;
}

export function fromFiltersQuery(query: QueryObject): Partial<FiltersModel> | null {
  const filtersModel: Partial<FiltersModel> = {};
  const setFilter = <K extends FilterGroupId>(id: K, value: FiltersModel[K]): void => {
    filtersModel[id] = value;
  };

  getOwnedGroups().forEach((group) => {
    if (group.control === 'budget') {
      const min = toAmount(query[BUDGET_MIN_KEY]);
      const max = toAmount(query[BUDGET_MAX_KEY]);
      if (min || max) setFilter(group.id, { min, max });

      return;
    }

    if (isDynamicFilterGroup(group)) {
      const ids = toKeys(query[`${FILTER_QUERY_PREFIX}${group.id}`]);
      if (ids.length) setFilter(group.id, ids);

      return;
    }

    if (group.control === 'checkbox') {
      if (isFlagSet(query[getFlagKey(group.id)]) || isFlagSet(query[group.id])) {
        setFilter(group.id, true);
      }

      return;
    }

    const selected = getGroupTokens(group).filter((token) => isFlagSet(query[`${FILTER_QUERY_PREFIX}${token}`]));
    if (!selected.length) return;

    if (group.control === 'radio') {
      setFilter(group.id, selected[0] as FiltersModel[typeof group.id]);

      return;
    }

    setFilter(group.id, selected as FiltersModel[typeof group.id]);
  });

  return Object.keys(filtersModel).length ? filtersModel : null;
}

export function getOwnedFilterQueryKeys(query: QueryObject): string[] {
  return Object.keys(query).filter((key) => key.startsWith(FILTER_QUERY_PREFIX));
}
